import { useMutation } from "convex/react";
import { api } from "../../../../convex/_generated/api";
import type { Id } from "../../../../convex/_generated/dataModel";

interface Cover {
  coverId: string;
  aspect: string;
  url: string;
}

interface Props {
  storyId: Id<"stories">;
  pageIndex?: number;
  coverId?: string;
  sessionId: string;
  covers?: Cover[];
}

/**
 * Native share handoff. We never embed user-authored Take overlay text in
 * the MVP (FR-019, plan.md). Card text is server-built English-only.
 *
 * When `covers` is provided we additionally surface one chip per cover so the
 * sharer can pick the aspect ratio that fits their target surface (TikTok 9:16,
 * Instagram 1:1, X 16:9). Each chip routes through the same mutation and
 * carries the chosen `coverId` for attribution.
 */
export function ShareButton({ storyId, pageIndex, coverId, sessionId, covers = [] }: Props) {
  const share = useMutation(api.sharing.share.createShareEvent);

  async function go(chosenCoverId?: string) {
    const { destinationUrl } = await share({
      storyId,
      pageIndex,
      coverId: chosenCoverId ?? coverId,
      destination: "x",
      sessionId,
    });
    const captureHook = (window as unknown as { __captureShareUrl?: (u: string) => void })
      .__captureShareUrl;
    if (typeof captureHook === "function") captureHook(destinationUrl);
    window.open(destinationUrl, "_blank", "noopener,noreferrer");
  }

  return (
    <div className="share-tray">
      <button
        type="button"
        className="share-button"
        onClick={() => go()}
        aria-label="Share with my take"
      >
        Share with my take
      </button>
      {covers.length > 0 && (
        <div className="share-covers" role="group" aria-label="Share with cover variant">
          {covers.map((c) => (
            <button
              key={c.coverId}
              type="button"
              className="share-cover"
              onClick={() => go(c.coverId)}
              aria-label={`Share ${c.aspect} cover`}
              title={`Share ${c.aspect} cover`}
              style={{ backgroundImage: `url("${c.url}")` }}
            >
              <span className="visually-hidden">{c.aspect}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
